import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useToast } from '../../../contexts-hooks/hooks/useToster';
import { useLogOut } from '../../../store/query/userAuthenticationQuery';
import { useChangePassword, useFirstLoginStatus } from '../../../store/query/userQuery';

export const useChangePasswordManagement = (onClose) => {
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setConfirmPassword] = useState(false);
  
  const { showToast } = useToast();
  const changePasswordMutation = useChangePassword();
  const updateFirstLoginStatusMutation = useFirstLoginStatus();
  const logOutMutation = useLogOut();

  const validationSchema = Yup.object({
    currentPassword: Yup.string().required("Current password is required"),
    newPassword: Yup.string()
      .min(8, "New password must be at least 8 characters long")
      .required("New password is required")
      .test(
        "not-same-as-current",
        "New password must be different from current password",
        function (value) {
          return value !== this.parent.currentPassword;
        }
      ),
    confirmNewPassword: Yup.string()
      .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
      .required("Confirm password is required"),
  });

  const formik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        setLoading(true);
        await changePasswordMutation.mutateAsync({
          passwordCurrent: values.currentPassword,
          password: values.newPassword,
          passwordConfirm: values.confirmNewPassword,
        });

        await updateFirstLoginStatusMutation.mutateAsync();
        logOutMutation.mutate();

        showToast("Password changed successfully", "success");
        onClose();
      } catch (error) {
        console.error("Password change error:", error);
        setErrors({
          submit:
            error.response?.data?.message ||
            "Failed to change password. Please try again.",
        });
        showToast(
          error.response?.data?.message || "Failed to change password",
          "error"
        );
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  const toggleCurrentPasswordVisibility = () => {
    setShowCurrentPassword((prev) => !prev);
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword((prev) => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setConfirmPassword((prev) => !prev);
  };

  return {
    loading,
    formik,
    showCurrentPassword,
    showNewPassword,
    showConfirmPassword,
    toggleCurrentPasswordVisibility,
    toggleNewPasswordVisibility,
    toggleConfirmPasswordVisibility,
  };
};
